// Every route in one file. Four tabs (Home, Planner, Social, Search), each with its own stack (re-tapping a tab pops it to
// the top, like the Swift app). Full-screen flows (cook mode, settings…) live in the root stack.
import React from 'react';
import { Platform } from 'react-native';
import { NavigationContainer, DefaultTheme, DarkTheme, createNavigationContainerRef, getStateFromPath, useNavigation, type NavigatorScreenParams } from '@react-navigation/native';
import { telegram } from './platform/telegram';
import { basePath } from './platform/basePath';
import { redirectPath } from './logic/links';
import { createNativeStackNavigator, type NativeStackNavigationProp } from '@react-navigation/native-stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { useTranslation } from 'react-i18next';
import Icon, { type IconName } from './components/Icon';
import { usePalette } from './theme';
import { useLabels } from './labels';
import { useSettings } from './store/settings';
import type { LibraryKind } from './screens/EntityListScreen';

import LibraryScreen from './screens/LibraryScreen';
import HomeScreen from './screens/HomeScreen';
import CountryCookbookScreen from './screens/CountryCookbookScreen';
import MealDiaryScreen from './screens/MealDiaryScreen';
import LogMealScreen from './screens/LogMealScreen';
import AllRecipesScreen from './screens/AllRecipesScreen';
import EntityListScreen from './screens/EntityListScreen';
import RecipeScreen from './screens/RecipeScreen';
import CookbookScreen from './screens/CookbookScreen';
import MealplanScreen from './screens/MealplanScreen';
import IngredientScreen from './screens/IngredientScreen';
import UtensilScreen from './screens/UtensilScreen';
import UnitScreen from './screens/UnitScreen';
import CookModeScreen from './screens/CookModeScreen';
import PlannerScreen from './screens/PlannerScreen';
import GroceryScreen from './screens/GroceryScreen';
import PantryScreen from './screens/PantryScreen';
import SearchScreen from './screens/SearchScreen';
import SettingsScreen from './screens/SettingsScreen';
import AccountScreen from './screens/AccountScreen';
import SyncScreen from './screens/SyncScreen';
import RedeemScreen from './screens/RedeemScreen';
import PersonalSettingsScreen from './screens/PersonalSettingsScreen';
import AppSettingsScreen from './screens/AppSettingsScreen';
import InfoScreen from './screens/InfoScreen';
import SuperScreen from './screens/SuperScreen';
import CheckoutScreen from './screens/CheckoutScreen';
import OnboardingScreen from './screens/OnboardingScreen';
import DietPlanScreen from './screens/DietPlanScreen';
import FriendsScreen from './screens/FriendsScreen';
import JoinScreen from './screens/JoinScreen';
import AiScreen from './screens/AiScreen';
import OAuthScreen from './screens/OAuthScreen';
import CreatorScreen from './screens/CreatorScreen';

/** Screens reachable from every tab. */
export type DetailParams = {
  Recipe: { id: string; edit?: boolean };
  CountryCookbook: { id: string };
  Cookbook: { id: string; edit?: boolean };
  Mealplan: { id: string; edit?: boolean };
  Ingredient: { id: string; edit?: boolean };
  Utensil: { id: string; edit?: boolean };
  Unit: { id: string; edit?: boolean };
  EntityList: { kind: LibraryKind };
  AllRecipes: undefined;
  Library: undefined;
  Grocery: undefined;
  Pantry: undefined;
  MealDiary: undefined;
};

type TabRoots = { PathHome: undefined; PlannerHome: undefined; SocialHome: { friendId?: string } | undefined; SearchHome: undefined };
type StackParams = DetailParams & TabRoots;

export type RootParams = {
  Tabs: NavigatorScreenParams<{ PathTab: undefined; PlannerTab: undefined; SocialTab: undefined; SearchTab: undefined }>;
  CookMode: { recipeId: string; cookFor?: number };
  LogMeal: { recipeId?: string };
  Settings: undefined;
  Account: undefined;
  Sync: { code?: string };
  Redeem: { code?: string };
  PersonalSettings: undefined;
  AppSettings: undefined;
  Info: { page: 'help' | 'legal' | 'about' | 'dietRules' | 'dietPlanMethod' };
  /** paid: back from the web checkout (Stripe adds ?paid=1). */
  Super: { paid?: string } | undefined;
  Checkout: { offer?: string; currency?: string } | undefined;
  Onboarding: undefined;
  DietPlan: undefined;
  /** A streak's invite link (/join?code=…). */
  Join: { code?: string } | undefined;
  /** MCP assistant connections, local helpers and proposals awaiting approval. */
  Ai: undefined;
  /** OAuth consent for assistants (Claude, ChatGPT): the query string of /oauth/authorize. */
  OAuthAuthorize: Record<string, string> | undefined;
  /** An approved creator's public profile (/@handle). */
  Creator: { handle: string };
};

export type AllParams = StackParams & RootParams;
export const useNav = () => useNavigation<NativeStackNavigationProp<AllParams>>();

const Root = createNativeStackNavigator<RootParams>();
const Tab = createBottomTabNavigator();

function detailScreens(Stack: ReturnType<typeof createNativeStackNavigator<StackParams>>) {
  return (
    <>
      <Stack.Screen name="Recipe" component={RecipeScreen} options={{ title: '' }} />
      <Stack.Screen name="CountryCookbook" component={CountryCookbookScreen} options={{ title: '' }} />
      <Stack.Screen name="Cookbook" component={CookbookScreen} options={{ title: '' }} />
      <Stack.Screen name="Mealplan" component={MealplanScreen} options={{ title: '' }} />
      <Stack.Screen name="Ingredient" component={IngredientScreen} options={{ title: '' }} />
      <Stack.Screen name="Utensil" component={UtensilScreen} options={{ title: '' }} />
      <Stack.Screen name="Unit" component={UnitScreen} options={{ title: '' }} />
      <Stack.Screen name="EntityList" component={EntityListScreen} options={{ title: '' }} />
      <Stack.Screen name="Library" component={LibraryScreen} options={{ title: '' }} />
      <Stack.Screen name="AllRecipes" component={AllRecipesScreen} options={{ title: '' }} />
      <Stack.Screen name="Grocery" component={GroceryScreen} options={{ title: '' }} />
      <Stack.Screen name="Pantry" component={PantryScreen} options={{ title: '' }} />
      <Stack.Screen name="MealDiary" component={MealDiaryScreen} options={{ title: '' }} />
    </>
  );
}

function makeStack(rootName: keyof TabRoots, Root: React.ComponentType<any>, title: string) {
  const Stack = createNativeStackNavigator<StackParams>();
  return function TabStack() {
    return (
      <Stack.Navigator initialRouteName={rootName} screenOptions={{ headerBackButtonDisplayMode: 'minimal', headerTitle: '' }}>
        <Stack.Screen name={rootName} component={Root} options={{ title }} />
        {detailScreens(Stack)}
      </Stack.Navigator>
    );
  };
}

function Tabs() {
  const { t } = useTranslation();
  const p = usePalette();
  const stacks = React.useMemo(
    () => ({
      PathTab: makeStack('PathHome', HomeScreen, t('tabs.path')),
      PlannerTab: makeStack('PlannerHome', PlannerScreen, t('tabs.planner')),
      SocialTab: makeStack('SocialHome', FriendsScreen, t('tabs.social')),
      SearchTab: makeStack('SearchHome', SearchScreen, t('tabs.search')),
    }),
    [t],
  );
  const labels = useLabels();
  const icon = (name: IconName) => ({ color, size }: { color: string; size: number }) => <Icon name={name} color={color} size={size} />;
  return (
    <Tab.Navigator
      screenOptions={{
        headerShown: false,
        tabBarActiveTintColor: p.tint,
        tabBarInactiveTintColor: p.secondaryText,
        // Icons only, unless labels are switched on for accessibility.
        tabBarShowLabel: labels,
        tabBarLabelStyle: { fontSize: 12, fontWeight: '600' },
        // The web tab bar has no safe-area padding of its own; give icon + label room.
        tabBarStyle: [{ backgroundColor: p.background, borderTopColor: p.border }, Platform.OS === 'web' ? { height: labels ? 62 : 56, paddingBottom: 6 } : null],
      }}>
      <Tab.Screen name="PathTab" component={stacks.PathTab} options={{ title: t('tabs.path'), tabBarIcon: icon('home') }} />
      <Tab.Screen name="PlannerTab" component={stacks.PlannerTab} options={{ title: t('tabs.planner'), tabBarIcon: icon('planner') }} />
      <Tab.Screen name="SocialTab" component={stacks.SocialTab} options={{ title: t('tabs.social'), tabBarIcon: icon('buddies') }} />
      <Tab.Screen name="SearchTab" component={stacks.SearchTab} options={{ title: t('tabs.search'), tabBarIcon: icon('search') }} />
    </Tab.Navigator>
  );
}

// Web/Telegram: real URLs and browser-history support (back button works like in the app).
const detailPaths = (prefix: string) => ({
  [`${prefix}Home`]: '',
  Recipe: 'recipe/:id',
  CountryCookbook: 'country/:id',
  Cookbook: 'cookbook/:id',
  Mealplan: 'mealplan/:id',
  Ingredient: 'ingredient/:id',
  Utensil: 'utensil/:id',
  Unit: 'unit/:id',
  EntityList: 'collection/:kind',
  AllRecipes: 'recipes',
  Library: 'library',
  Grocery: 'grocery',
  Pantry: 'pantry',
  MealDiary: 'diary',
});
const linking = {
  enabled: Platform.OS === 'web',
  prefixes: [],
  getStateFromPath: (path: string, options: Parameters<typeof getStateFromPath>[1]) => getStateFromPath(redirectPath(path), options),
  config: {
    path: basePath,
    screens: {
      Tabs: {
        screens: {
          PathTab: { path: '', screens: detailPaths('Path') },
          PlannerTab: { path: 'planner', screens: detailPaths('Planner') },
          SocialTab: { path: 'social', screens: detailPaths('Social') },
          SearchTab: { path: 'search', screens: detailPaths('Search') },
        },
      },
      CookMode: 'cook/:recipeId',
      LogMeal: 'log-meal',
      Settings: 'settings',
      Account: 'settings/account',
      Sync: 'sync',
      Redeem: 'redeem',
      PersonalSettings: 'settings/personal',
      AppSettings: 'settings/app',
      Info: 'info/:page',
      Super: 'super',
      Checkout: 'checkout',
      Onboarding: 'welcome',
      DietPlan: 'diet-plan',
      Join: 'join',
      Ai: 'ai',
      OAuthAuthorize: 'oauth/authorize',
      Creator: 'creator/:handle',
    },
  },
};

export const navigationRef = createNavigationContainerRef<AllParams>();

export default function Navigation() {
  const p = usePalette();
  const { t } = useTranslation();
  const base = p.scheme === 'dark' ? DarkTheme : DefaultTheme;
  const theme = { ...base, colors: { ...base.colors, primary: p.tint, background: p.background, card: p.background, text: p.text, border: p.border } };
  return (
    <NavigationContainer
      ref={navigationRef}
      onReady={() => {
        // First launch: show the tutorial once (works with deep links too).
        // (Not over sync, redeem, checkout, streak, assistant or creator links: finishing the tutorial would replace the requested flow.)
        if (!useSettings.getState().onboardingSeen && !['Sync', 'Redeem', 'Checkout', 'Join', 'Ai', 'OAuthAuthorize', 'Creator'].includes(navigationRef.getCurrentRoute()?.name ?? '')) navigationRef.navigate('Onboarding');
      }} theme={theme} linking={linking as never} documentTitle={{ formatter: () => 'Chefi' }} onStateChange={() => telegram.syncBackButton(navigationRef.canGoBack(), () => navigationRef.goBack())}>
      <Root.Navigator initialRouteName="Tabs" screenOptions={{ headerBackButtonDisplayMode: 'minimal', headerTitle: '' }}>
        <Root.Screen name="Tabs" component={Tabs} options={{ headerShown: false }} />
        <Root.Screen name="CookMode" component={CookModeScreen} options={{ presentation: 'fullScreenModal', headerShown: false }} />
        <Root.Screen name="LogMeal" component={LogMealScreen} options={{ presentation: 'modal', title: t('logMeal.title') }} />
        <Root.Screen name="Settings" component={SettingsScreen} options={{ title: t('settings.title') }} />
        <Root.Screen name="Account" component={AccountScreen} options={{ title: t('settings.account') }} />
        <Root.Screen name="Sync" component={SyncScreen} options={{ title: t('sync.title') }} />
        <Root.Screen name="Redeem" component={RedeemScreen} options={{ title: t('plan.redeemTitle') }} />
        <Root.Screen name="PersonalSettings" component={PersonalSettingsScreen} options={{ title: t('settings.personal') }} />
        <Root.Screen name="AppSettings" component={AppSettingsScreen} options={{ title: t('settings.app') }} />
        <Root.Screen name="Info" component={InfoScreen} options={{ title: '' }} />
        <Root.Screen name="Checkout" component={CheckoutScreen} options={{ title: t('checkout.title') }} />
      <Root.Screen name="Super" component={SuperScreen} options={{ presentation: 'modal', title: t('super.title') }} />
        <Root.Screen name="Creator" component={CreatorScreen} options={{ title: '' }} />
        <Root.Screen name="Join" component={JoinScreen} options={{ title: t('streaks.joinTitle') }} />
        <Root.Screen name="Ai" component={AiScreen} options={{ title: t('ai.title') }} />
        <Root.Screen name="OAuthAuthorize" component={OAuthScreen} options={{ title: t('oauth.title') }} />
        <Root.Screen name="Onboarding" component={OnboardingScreen} options={{ headerShown: false, presentation: 'fullScreenModal' }} />
        <Root.Screen name="DietPlan" component={DietPlanScreen} options={{ headerShown: false, presentation: 'fullScreenModal' }} />
      </Root.Navigator>
    </NavigationContainer>
  );
}
